import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';

const BlogList = () => {
  const [blogs, setBlogs] = useState<any[]>([]);

  useEffect(() => {
    // Backend API exactly as you had it
    axios.get('https://www.projectinfotech.in/api/blogs')
      .then(res => setBlogs(res.data))
      .catch(err => console.error("Error fetching blogs", err));
  }, []);

  // Sort blogs so the latest one comes first
  const sortedBlogs = [...blogs].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <div className="bg-gray-50 text-gray-900 font-sans min-h-screen pb-20">
      <Helmet>
        <title>Tech Blog & Tutorials | PIT Institute</title>
        <meta name="description" content="Read the latest tech blogs, tutorials, and insights on Full Stack Development, AI, and Software Engineering." />
      </Helmet>

      {/* Hero Section - FIXED PADDING */}
      {/* pt-28 and md:pt-36 pushes the text down so it doesn't hide behind the navbar */}
      <header className="bg-black text-white pt-28 pb-12 md:pt-36 md:pb-20 text-center border-b-4 border-red-600 px-4">
        <h1 className="text-3xl md:text-5xl font-extrabold mb-3 md:mb-4 font-aligran">
          Explore Our <span className="text-red-600">Tech Blog</span>
        </h1>
        <p className="text-gray-300 max-w-2xl mx-auto text-sm md:text-lg">
          Insights, tutorials, and career advice directly from industry experts.
        </p>
      </header>

      {/* Blogs Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-10 md:mt-16">
        {sortedBlogs.length === 0 ? (
          <div className="text-center text-gray-500 py-20">No blogs published yet. Stay tuned!</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            {sortedBlogs.map((blog, i) => (
              <motion.div 
                key={blog.id}
                className="bg-white rounded-[20px] shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden flex flex-col group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                {/* Image Section */}
                <div className="h-28 sm:h-36 md:h-44 overflow-hidden relative p-2 md:p-3 pb-0">
                  <img 
                    src={blog.imageUrl || 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800'} 
                    alt={blog.imageAlt || blog.title} 
                    className="w-full h-full object-cover rounded-[14px] group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                {/* Content Section */}
                <div className="p-4 md:p-5 flex flex-col flex-grow">
                  <div className="flex items-center gap-1.5 text-[10px] md:text-xs text-red-600 font-bold mb-2">
                    <Calendar size={12} className="md:w-[14px] md:h-[14px]" /> 
                    {new Date(blog.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </div>
                  
                  <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 line-clamp-3 leading-tight md:leading-snug">
                    {blog.title}
                  </h3>
                  
                  {/* Footer/Link Section */}
                  <div className="mt-auto pt-3 md:pt-4 border-t border-gray-50">
                    <Link 
                      to={`/blog/${blog.slug}`} 
                      className="flex items-center text-[11px] md:text-sm text-red-600 font-bold hover:text-red-700 transition-colors group/link"
                    >
                      Read Full Article 
                      <ArrowRight size={14} className="ml-1 md:ml-2 transform group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;